// ── BEMANNINGSNYCKELN — modellen ──────────────────────────────────
// Ett rent räknelager utan React, så att Lednings-vyn och (senare)
// avdelningarnas "För planering" använder EXAKT samma modell.
//
//   prognos          = ifjol samma veckodag × pace-faktor
//   lönebudget       = prognos × mål-personal%
//   rörligt utrymme  = lönebudget − fasta löner
//   tillåtna timmar  = rörligt utrymme ÷ rörlig kr-per-timme
//
// VARFÖR fast och rörligt måste skiljas åt: daily_units lön per
// Resultatenhet innehåller både stämplad lön (Personalkollen, har timmar)
// och styrfilens fasta löner (utspridda per dag, HAR INGA timmar). Slår man
// ihop dem blir kr/timme meningslös i lågsäsong — Sportcenter jun–aug 2026:
// 866 kr/h all-in mot 333 kr/h rörligt, eftersom nästan hela lönen är fast.
//
// Personal% räknas som i resten av portalen: hela lönen (fast + rörlig)
// ÷ intäkt. Målet gäller alltså totalen, men bara den rörliga delen går
// att schemalägga bort.

// Avdelningarna. `rev` = Resultatenhet-namn i daily_units, `tim` = enhetens
// namn i daily_hours `order` (som i sin tur summerar Personalkollen-enheter).
// Brasserie: Vävstugans intäkt hör hit eftersom dagpersonalen ("Kerstin Dag")
// ligger i samma timserie — annars hänger dagens lön på kvällens intäkt.
export const AVDELNINGAR = [
  { id: 'logi',        namn: 'Hotell Logi',        rev: ['Hotell Logi'],                    tim: 'Hotell Logi',        pace: true,  farg: '#1B2A4A' },
  { id: 'hotellrest',  namn: 'Hotellrestaurangen', rev: ['Hotellrestaurangen'],             tim: 'Hotellrestaurangen', pace: true,  farg: '#2E6E8E' },
  { id: 'brasserie',   namn: 'Brasserie',          rev: ['Brasserie Kerstin', 'Vävstugan'], tim: 'Brasserie Kerstin',  pace: false, farg: '#7A3B1C' },
  { id: 'knallen',     namn: 'Knallen',            rev: ['Knallen'],                        tim: 'Knallen',            pace: false, farg: '#6B4E2E' },
  { id: 'sportcenter', namn: 'Sportcenter',        rev: ['Sportcenter'],                    tim: 'Sportcenter',        pace: false, farg: '#1A3D2B' },
  { id: 'stugservice', namn: 'Stugservice',        rev: ['Stugservice'],                    tim: 'Stugservice',        pace: false, farg: '#1D6A5F' },
];

// Pace-faktorn tillåts aldrig springa iväg: en fjolårsdag med 3 gäster får
// inte ge 12× bemanning. Utanför spannet kapas den och flaggas.
export const PACE_MIN = 0.3;
export const PACE_MAX = 3.5;
// Under så här många gästnätter ifjol är kvoten ren slump → faktor 1.
const PACE_MIN_UNDERLAG = 20;

export const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// daily_units är ~6 000 rader (dag × enhet). Slå upp per dag en gång i
// stället för att skanna om arrayen för varje dag och avdelning.
export function indexeraIntakt(units) {
  const ix = {};
  for (const r of units || []) {
    const d = ix[r.datum] || (ix[r.datum] = {});
    d[r.enhet] = (d[r.enhet] || 0) + (r.intakt || 0);
  }
  return ix;
}

const intaktFor = (ix, datum, enheter) =>
  enheter.reduce((s, e) => s + ((ix[datum] || {})[e] || 0), 0);

export function skift(d, dagar) {
  const n = new Date(d);
  n.setDate(n.getDate() + dagar);
  return n;
}

// Måndagen i datumets ISO-vecka
export function mandag(d) {
  const n = new Date(d);
  n.setHours(12, 0, 0, 0);
  return skift(n, -((n.getDay() + 6) % 7));
}

// Fjolårets matchande dag = −364 dgr (52 veckor → samma veckodag)
export const ifjolDag = (d) => skift(d, -364);

// ── NYCKELTAL PER AVDELNING (rörlig kr/timme + fast lön per dag) ───
// Räknas ur ett bakåtblickande fönster som slutar `tillDatum`. Fönstret
// vidgas automatiskt när säsongen gjort att avdelningen knappt haft timmar
// (Sportcenter på sommaren, Knallen i maj) — annars blir kr/timme brus.
export function beraknaNycklar(units, hours, tillDatum = new Date()) {
  const timIdx = Object.fromEntries((hours.order || []).map((n, i) => [n, i]));
  const ut = {};

  for (const avd of AVDELNINGAR) {
    const i = timIdx[avd.tim];
    let vald = null;

    for (const dagar of [90, 365]) {
      // Inklusivt fönster på exakt `dagar` dagar — annars delas fasta löner
      // för 91 dagar på 90 och kryper uppåt.
      const from = iso(skift(tillDatum, -(dagar - 1)));
      const till = iso(tillDatum);

      let timmar = 0, pkLon = 0, totLon = 0;
      if (i != null) {
        for (const [datum, rad] of Object.entries(hours.byDatum || {})) {
          if (datum < from || datum > till) continue;
          timmar += rad[i] || 0;
          if (hours.harLon) pkLon += (hours.lonByDatum[datum] || [])[i] || 0;
        }
      }
      for (const r of units) {
        if (r.datum < from || r.datum > till) continue;
        if (avd.rev.includes(r.enhet)) totLon += Math.abs(r.lon || 0);
      }

      // Utan lönekolumner i cachen finns ingen fast/rörlig-uppdelning att
      // göra — kör all-in och låt vyn skylta med att siffran är trubbig.
      const rorligLon = hours.harLon ? pkLon : totLon;
      const fastTotal = hours.harLon ? Math.max(0, totLon - pkLon) : 0;

      if (timmar >= 40) {
        vald = {
          krPerTim: Math.round(rorligLon / timmar),
          fastPerDag: Math.round(fastTotal / dagar),
          fonster: dagar,
          timmarIFonster: Math.round(timmar),
          allin: !hours.harLon,
        };
        break;
      }
    }

    ut[avd.id] = vald || { krPerTim: null, fastPerDag: 0, fonster: null, timmarIFonster: 0, allin: !hours.harLon };
  }
  return ut;
}

// ── FJOLÅRETS NIVÅ (utgångsläget att förbättra) ───────────────────
// Vad fjolårets faktiska bemanning hade kostat med DAGENS lönenivå, som
// andel av fjolårets intäkt. Det är den ärliga referensen för ett mål —
// inte ett påhittat branschtal.
export function fjolarsNiva(units, hours, nycklar, from, till) {
  const timIdx = Object.fromEntries((hours.order || []).map((n, i) => [n, i]));
  const ix = indexeraIntakt(units);
  const ut = {};
  for (const avd of AVDELNINGAR) {
    const n = nycklar[avd.id];
    const i = timIdx[avd.tim];
    let intakt = 0, timmar = 0, dagar = 0;
    for (let d = new Date(from); d <= till; d = skift(d, 1)) {
      const k = iso(ifjolDag(d));
      dagar++;
      timmar += ((hours.byDatum || {})[k] || [])[i] || 0;
      intakt += intaktFor(ix, k, avd.rev);
    }
    const kostnad = timmar * (n.krPerTim || 0) + n.fastPerDag * dagar;
    ut[avd.id] = { intakt, timmar, kostnad, pct: intakt > 0 ? kostnad / intakt : null };
  }
  return ut;
}

// Förslag på mål: fjolårets nivå minus 5 procentenheter, aldrig under 15%.
// Ett förslag — inte ett facit. Sätts målet i Bemanning-fliken vinner det.
export function malForslag(niva) {
  if (niva == null || !isFinite(niva)) return null;
  return Math.max(0.15, Math.round((niva - 0.05) * 100) / 100);
}

// ── VECKORNA ──────────────────────────────────────────────────────
// Returnerar en rad per vecka och avdelning med prognos, tillåtna timmar,
// fjolårets timmar och (om inmatat) planerade timmar + avvikelse.
export function beraknaVeckor({ units, hours, bel, nycklar, mal = {}, planerat = {}, start, veckor = 4 }) {
  const timIdx = Object.fromEntries((hours.order || []).map((n, i) => [n, i]));
  const byDatum = bel?.byDatum || {};
  const ix = indexeraIntakt(units);
  const ut = [];

  for (let w = 0; w < veckor; w++) {
    const from = skift(mandag(start), w * 7);
    const till = skift(from, 6);
    const dagar = Array.from({ length: 7 }, (_, i) => skift(from, i));

    // Pace per VECKA, inte per dag: dagskvoter hoppar för mycket för att
    // vara planeringsunderlag. Bekräftade gästnätter på båda sidor —
    // obekräftade offerter är uppsida, inte något man bemannar för.
    let gastNu = 0, gastIfjol = 0;
    for (const d of dagar) {
      gastNu    += (byDatum[iso(d)]            || {}).rumG || 0;
      gastIfjol += (byDatum[iso(ifjolDag(d))]  || {}).rumG || 0;
    }
    const raPace = gastIfjol > 0 ? gastNu / gastIfjol : null;
    const tunnt  = gastIfjol < PACE_MIN_UNDERLAG;
    const pace   = (raPace == null || tunnt) ? 1 : Math.min(PACE_MAX, Math.max(PACE_MIN, raPace));
    const kapad  = raPace != null && !tunnt && (raPace < PACE_MIN || raPace > PACE_MAX);

    const veckoNr = veckoNummer(from);
    const nyckel  = veckoNyckel(from);
    const rader = [];

    for (const avd of AVDELNINGAR) {
      const n = nycklar[avd.id];
      const i = timIdx[avd.tim];
      const f = avd.pace ? pace : 1;

      let ifjolIntakt = 0, ifjolTim = 0;
      for (const d of dagar) {
        const k = iso(ifjolDag(d));
        ifjolTim += ((hours.byDatum || {})[k] || [])[i] || 0;
        ifjolIntakt += intaktFor(ix, k, avd.rev);
      }

      const prognos   = ifjolIntakt * f;
      const malPct    = mal[avd.id] ?? null;
      const fastVecka = n.fastPerDag * 7;
      const budget    = malPct != null ? prognos * malPct : null;
      const rorligt   = budget != null ? budget - fastVecka : null;
      const tillatna  = (rorligt != null && n.krPerTim) ? Math.max(0, rorligt / n.krPerTim) : null;

      // Fasta löner större än hela lönebudgeten → noll rörliga timmar. Det
      // är inte ett räknefel utan ett besked: avdelningen bär sin fasta
      // bemanning på för liten intäkt.
      const fastAterUpp = rorligt != null && rorligt <= 0;

      const plan = (planerat[nyckel] || {})[avd.id];
      const planTim = typeof plan === 'number' ? plan : null;

      rader.push({
        avd, prognos, ifjolIntakt, ifjolTim, malPct, fastVecka, budget, tillatna, fastAterUpp,
        planTim,
        avvikelse: (planTim != null && tillatna != null) ? planTim - tillatna : null,
        // Stängt ifjol → ingen prognos går att göra på fjolårsdata
        stangt: ifjolIntakt < 1000 && ifjolTim < 2,
        krPerTim: n.krPerTim,
      });
    }

    // Summan får ALDRIG visa 0 där svaret är "vet ej". Utan mål finns inget
    // tak — då ska summan vara null hela vägen, annars läser man 0 h som ett
    // uträknat tak och avvikelsen som grön när ingenting är beräknat.
    const harTillatna = rader.some(r => r.tillatna != null);
    const sumTillatna = harTillatna ? rader.reduce((s, r) => s + (r.tillatna || 0), 0) : null;
    const sumPlan = rader.some(r => r.planTim != null)
      ? rader.reduce((s, r) => s + (r.planTim || 0), 0) : null;

    ut.push({
      w, from, till, veckoNr, nyckel,
      pace, raPace, kapad, tunnt, gastNu, gastIfjol,
      rader,
      summa: {
        prognos:  rader.reduce((s, r) => s + (r.prognos || 0), 0),
        tillatna: sumTillatna,
        ifjolTim: rader.reduce((s, r) => s + (r.ifjolTim || 0), 0),
        planTim:  sumPlan,
        avvikelse: (sumTillatna != null && sumPlan != null) ? sumPlan - sumTillatna : null,
      },
    });
  }
  return ut;
}

// ISO-vecka ur ett datum (samma räkning som utils.getISOWeek, men lokal
// här så modellen kan användas fristående)
export function veckoNummer(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() + 3 - ((x.getDay() + 6) % 7));
  const v1 = new Date(x.getFullYear(), 0, 4);
  return 1 + Math.round(((x - v1) / 86400000 - 3 + ((v1.getDay() + 6) % 7)) / 7);
}

// Nyckel för sparade planerade timmar. ISO-veckoåret tas ur torsdagen i
// veckan — vecka 1 kan ligga i december föregående kalenderår.
export function veckoNyckel(d) {
  const torsdag = skift(mandag(d), 3);
  return `${torsdag.getFullYear()}-W${String(veckoNummer(d)).padStart(2, '0')}`;
}
